import { Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import type { RouteRecord } from 'vite-react-ssg';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/backoffice/ProtectedRoute';
import { BackofficeLayout } from './components/backoffice/BackofficeLayout';
import ScrollToTop from './components/ScrollToTop';
import { CookieBanner } from './components/CookieBanner';
import Layout from './components/layout/Layout';
import AnalyticsTracker from './components/AnalyticsTracker';
import { lazyWithReload } from './utils/lazyWithReload';

// Public pages — statically imported so they are pre-rendered
import Home from './pages/Home';
import Services, { servicesLoader } from './pages/Services';
import About from './pages/About';
import Reservation from './pages/Reservation';
import GiftVouchers from './pages/GiftVouchers';
import Packages from './pages/Packages';
import Faq, { faqLoader } from './pages/Faq';
import Contact from './pages/Contact';
import Blog from './pages/Blog';
import BlogArticle from './pages/BlogArticle';
import Legal from './pages/Legal';
import CGV from './pages/CGV';
import PrivacyPolicy from './pages/PrivacyPolicy';
import PaymentStatus from './pages/PaymentStatus';
import NotFound from './pages/NotFound';

// Backoffice pages — lazy loaded, not pre-rendered.
// lazyWithReload : recharge la page si un chunk a été invalidé par un déploiement.
const BackofficeLogin = lazyWithReload(() =>
  import('./pages/backoffice/Login').then(m => ({ default: m.BackofficeLogin }))
);
const ResetPassword = lazyWithReload(() =>
  import('./pages/backoffice/ResetPassword').then(m => ({ default: m.ResetPassword }))
);
const BackofficeDashboard = lazyWithReload(() =>
  import('./pages/backoffice/Dashboard').then(m => ({ default: m.BackofficeDashboard }))
);
const BookingsCalendar = lazyWithReload(() => import('./pages/backoffice/BookingsCalendar'));
const BackofficeBookings = lazyWithReload(() =>
  import('./pages/backoffice/Bookings').then(m => ({ default: m.BackofficeBookings }))
);
const AvailabilityManager = lazyWithReload(() => import('./pages/backoffice/AvailabilityManager'));
const BackofficePayments = lazyWithReload(() => import('./pages/backoffice/Payments'));
const ServiceManager = lazyWithReload(() =>
  import('./pages/backoffice/ServiceManager').then(m => ({ default: m.ServiceManager }))
);
const RoomClosures = lazyWithReload(() => import('./pages/backoffice/RoomClosures'));
const SystemSettings = lazyWithReload(() => import('./pages/backoffice/SystemSettings'));
const GiftVouchersManager = lazyWithReload(() => import('./pages/backoffice/GiftVouchersManager'));
const PackagesManager = lazyWithReload(() => import('./pages/backoffice/PackagesManager'));
const PromoCodesManager = lazyWithReload(() => import('./pages/backoffice/PromoCodesManager'));
const BlogManager = lazyWithReload(() => import('./pages/backoffice/BlogManager'));
const SiteUpdates = lazyWithReload(() => import('./pages/backoffice/SiteUpdates'));
const FaqManager = lazyWithReload(() => import('./pages/backoffice/FaqManager'));

/**
 * Root wrapper: provides HelmetProvider, AuthProvider, analytics and cookie
 * banner. BrowserRouter is injected by vite-react-ssg — this component only
 * needs to render <Outlet /> for the child routes.
 */
function RootWrapper() {
  return (
    <AuthProvider>
      <AnalyticsTracker />
      <ScrollToTop />
      <CookieBanner />
      <Outlet />
    </AuthProvider>
  );
}

export const routes: RouteRecord[] = [
  {
    path: '/',
    element: <RootWrapper />,
    children: [
      // Public routes — wrapped by the public Layout (navbar + footer)
      {
        path: '/',
        element: <Layout />,
        children: [
          { index: true, element: <Home /> },
          { path: 'services', element: <Services />, loader: servicesLoader },
          { path: 'a-propos', element: <About /> },
          { path: 'reservation', element: <Reservation /> },
          { path: 'bons-cadeaux', element: <GiftVouchers /> },
          { path: 'forfaits', element: <Packages /> },
          { path: 'paiement', element: <PaymentStatus /> },
          { path: 'blog', element: <Blog /> },
          // Route DYNAMIQUE : pas de loader ni de getStaticPaths. Le contenu
          // éditorial change entre deux builds ; on le charge en live côté client
          // (cf. BlogArticle). L'attacher au SSG ferait servir des données figées
          // du build en navigation interne → articles récents « introuvables ».
          { path: 'blog/:slug', element: <BlogArticle /> },
          { path: 'faq', element: <Faq />, loader: faqLoader },
          { path: 'contact', element: <Contact /> },
          { path: 'mentions-legales', element: <Legal /> },
          { path: 'cgv', element: <CGV /> },
          { path: 'privacy-policy', element: <PrivacyPolicy /> },
          { path: '*', element: <NotFound /> },
        ],
      },

      // Backoffice routes — lazy loaded, excluded from SSG
      {
        path: 'backoffice/login',
        element: <Suspense fallback={null}><BackofficeLogin /></Suspense>,
      },
      {
        path: 'backoffice/reset-password',
        element: <Suspense fallback={null}><ResetPassword /></Suspense>,
      },
      {
        path: 'backoffice',
        element: (
          <ProtectedRoute>
            <BackofficeLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: <Suspense fallback={null}><BackofficeDashboard /></Suspense>,
          },
          {
            path: 'dashboard',
            element: <Suspense fallback={null}><BackofficeDashboard /></Suspense>,
          },
          {
            path: 'bookings',
            element: <Suspense fallback={null}><BookingsCalendar /></Suspense>,
          },
          {
            path: 'bookings-list',
            element: <Suspense fallback={null}><BackofficeBookings /></Suspense>,
          },
          {
            path: 'availabilities',
            element: <Suspense fallback={null}><AvailabilityManager /></Suspense>,
          },
          {
            path: 'room-closures',
            element: <Suspense fallback={null}><RoomClosures /></Suspense>,
          },
          {
            path: 'payments',
            element: <Suspense fallback={null}><BackofficePayments /></Suspense>,
          },
          {
            path: 'gift-vouchers',
            element: <Suspense fallback={null}><GiftVouchersManager /></Suspense>,
          },
          {
            path: 'packages',
            element: <Suspense fallback={null}><PackagesManager /></Suspense>,
          },
          {
            path: 'services',
            element: <Suspense fallback={null}><ServiceManager /></Suspense>,
          },
          {
            path: 'system-settings',
            element: <Suspense fallback={null}><SystemSettings /></Suspense>,
          },
          {
            path: 'promo-codes',
            element: <Suspense fallback={null}><PromoCodesManager /></Suspense>,
          },
          {
            path: 'blog',
            element: <Suspense fallback={null}><BlogManager /></Suspense>,
          },
          {
            path: 'faq',
            element: <Suspense fallback={null}><FaqManager /></Suspense>,
          },
          {
            path: 'site-updates',
            element: <Suspense fallback={null}><SiteUpdates /></Suspense>,
          },
        ],
      },
    ],
  },
];
